import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReportsTable } from './ReportGroup';
import TrueImpactModal, {
  modalHeightAutoStyles,
  modalTitleStyles,
} from 'shared/design-system/components/TrueImpactModal';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import { Program } from '../Reports/NewReportDialog';
import {
  mapReportPrograms,
  ReportWithProgramYears,
} from './NewReport/NewReportContent';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  cloneReport,
  getReportsForProgram,
  ReportsForProgram,
  SuccessResponse,
} from './NewReport/api';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { useClient } from 'shared/apollo';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { adjustStartAndEndDates } from './NewReport/utils';
import SuccessMessage from './NewReport/SuccessMessage';
import { useHistory } from 'react-router-dom';
import { getProgramNameUrl } from '../ReportBuilder/Components/Budget/utils';

interface CloneModalProps {
  report: ReportsTable;
  programsList: RequestState<{
    allPrograms: Program[];
  }>;
  handleClose: () => unknown;
}

const StyledDiv = styled.div`
  padding: ${SPACERS.large};
  .dropdown-wrapper {
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.tiny};
  }
`;

const currentYear = new Date().getFullYear();
const getYearsArray = (minYear: number, maxYear: number) => {
  const years: number[] = [];
  for (let index = minYear; index <= maxYear; index++) {
    years.push(index);
  }
  return years;
};
export const tenYearsList = getYearsArray(currentYear - 10, currentYear + 10);

const CloneModal: React.FC<CloneModalProps> = ({
  report,
  programsList,
  handleClose,
}) => {
  const client = useClient();
  const { push } = useHistory();
  const [selected, setSelected] = useState<{
    year: number | undefined;
    program: Program | undefined;
  }>({
    year: undefined,
    program: report.program,
  });

  const [reportsForProgram, makeReportsForProgramRequest] = useGraphQLRequest<{
    reports: ReportsForProgram[];
  }>();
  const [clonedReport, makeClonedReportRequest] = useGraphQLRequest<
    SuccessResponse<'newReport'>
  >();

  useEffect(() => {
    if (!client) return;
    reportsForProgram.type === 'REQUEST_INIT' &&
      makeReportsForProgramRequest(getReportsForProgram(client));
  }, [client, reportsForProgram]);

  const handleGoToReport = useCallback(() => {
    if (clonedReport.type !== 'REQUEST_SUCCESS') return;
    push(
      `/npo/report-builder/${getProgramNameUrl(selected.program?.name)}/${
        (clonedReport.data.newReport as {
          id: number;
          success: true;
        }).id
      }`,
    );
  }, [clonedReport, selected.program]);

  const handleProgramChange = useCallback(
    (program: Program | undefined) => {
      setSelected({
        program,
        year: undefined,
      });
    },
    [setSelected],
  );
  const handleReportChange = useCallback(
    (year: number | undefined) => {
      setSelected(s => ({
        program: s.program,
        year,
      }));
    },
    [setSelected],
  );

  const handleClone = useCallback(() => {
    if (!client || !selected.program || !selected.year) return;
    const dates = adjustStartAndEndDates(
      report.startDate,
      report.endDate,
      selected.year,
    );

    const ignoreUpdateAfter = true;

    makeClonedReportRequest(
      cloneReport(
        client,
        report.dbId,
        selected.program,
        selected.year,
        dates.startDate,
        dates.endDate,
        ignoreUpdateAfter,
      ),
    );
  }, [report, selected, client, makeClonedReportRequest]);

  const reports: ReportWithProgramYears = useMemo(() => {
    if (reportsForProgram.type !== 'REQUEST_SUCCESS') return {};
    return mapReportPrograms(reportsForProgram.data?.reports).reportPrograms;
  }, [reportsForProgram]);

  const isLoading =
    reportsForProgram.type === 'REQUEST_INIT' ||
    reportsForProgram.type === 'REQUEST_START' ||
    programsList.type === 'REQUEST_INIT' ||
    programsList.type === 'REQUEST_START' ||
    clonedReport.type === 'REQUEST_START';
  const programOptions = programsList.data?.allPrograms || [];

  const cloneSuccess = clonedReport.type === 'REQUEST_SUCCESS';

  return (
    <TrueImpactModal
      open={true}
      maxWidth="700px"
      onClose={handleClose}
      errorText={
        clonedReport.type === 'REQUEST_ERROR'
          ? clonedReport.error.message
          : undefined
      }
      title="Clone Report"
      addStyles={modalHeightAutoStyles + modalTitleStyles}
      next={{
        onClick: cloneSuccess ? handleGoToReport : handleClone,
        text: cloneSuccess ? 'Yes, take me there' : 'Clone',
        disabled: isLoading || !selected.program || !selected.year,
        isLoading: clonedReport.type === 'REQUEST_START',
      }}
      previous={
        cloneSuccess
          ? undefined
          : {
              onClick: handleClose,
              text: 'Cancel',
              disabled: false,
              isLoading: false,
            }
      }
    >
      {isLoading ? (
        <TrueImpactLoading />
      ) : cloneSuccess ? (
        <SuccessMessage
          heading="Your report was successfully cloned!"
          subHeading={{
            one: 'Would you like to begin updating your report now?',
          }}
        />
      ) : (
        <StyledDiv>
          <TrueImpactText>
            Are you sure you want to clone this report?
            <br />
            <strong>
              {report.program.name} ({report.year})
            </strong>
          </TrueImpactText>
          <div className="dropdown-wrapper">
            <TrueImpactDropdown
              value={selected.program}
              options={programOptions}
              renderValue={p => p.name || 'Please select your program'}
              getId={p => p.Id}
              getLabel={p => p.name}
              native={true}
              onChange={handleProgramChange}
              onClear={undefined}
              getItemDisability={undefined}
              name="Please select your program"
            />
          </div>
          <div>
            <TrueImpactDropdown
              disabled={!selected.program}
              value={selected.year}
              options={tenYearsList}
              getId={y => y}
              getLabel={y => y}
              getItemDisability={y =>
                Boolean(selected.program && reports[selected.program.Id]?.[y])
              }
              native={true}
              onChange={handleReportChange}
              onClear={undefined}
              name="Please select your report year"
            />
          </div>
        </StyledDiv>
      )}
    </TrueImpactModal>
  );
};

export default React.memo(CloneModal);
